import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AustralianState, PageStatus } from '@urbanx/agx-ui-components';
import { SubmitForm } from 'services';
import { FormSubmissionResponse } from 'services/functions/campaigns/submitForm';
import useSelectedCampaign from './useSelectedCampaign';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useContext } from 'react';
import {
  CampaignsApiContext,
  Form,
  FormConfigurationRequestFormType,
  FormSaveResult,
  FormSubmissionRequestFormType,
  FormSubmissionRequestState,
  FormType,
} from '@urbanx/agx.campaigns.client';

interface SaveFormOptions {
  onSuccess: (response?: FormSaveResult) => void;
  onError: () => void;
}

interface SubmitFormOptions {
  onSuccess: (response?: FormSubmissionResponse) => void;
  onError: () => void;
}

const useFormConfig = (
  formType: FormType
): {
  formConfig?: Form;
  isLoading: boolean;
  isFetched: boolean;
  saveForm: (formValues: object, pageStatuses: PageStatus[], options: SaveFormOptions) => void;
  submitForm: (formValues: object, notifyAgent: boolean, options: SubmitFormOptions) => void;
} => {
  const { getAuthToken } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { campaign } = useSelectedCampaign();
  const formConfigQueryKey = ['form-config', formType, campaign?.campaignId];

  const campaignsApi = useContext(CampaignsApiContext);

  const {
    data: formConfig,
    isFetched,
    isLoading,
  } = useQuery<Form | undefined>({
    queryKey: formConfigQueryKey,
    queryFn: async () =>
      await campaignsApi.staffGetFormConfiguration({
        campaignId: campaign?.campaignId,
        formType: formType as unknown as FormConfigurationRequestFormType,
      }),
    gcTime: 0,
    enabled: !!campaign?.campaignId,
  });

  const saveForm = async (
    formValues: object,
    pageStatuses: PageStatus[],
    options: SaveFormOptions
  ) => {
    if (!campaign?.campaignId || !campaign?.state) return;

    try {
      const result = await campaignsApi.staffSaveForm({
        campaignId: campaign.campaignId,
        formType: formType as unknown as FormSubmissionRequestFormType,
        state: campaign.state as unknown as FormSubmissionRequestState,
        formData: formValues,
        pageStatuses: pageStatuses,
      });

      if (!result) return;

      await queryClient.invalidateQueries({
        queryKey: formConfigQueryKey,
      });

      if (options?.onSuccess) options.onSuccess(result!);
    } catch (error) {
      console.error('Error saving form:', error);
    }
  };

  const { mutate: submitFormValues } = useMutation({ mutationFn: SubmitForm });

  const submitForm = (formValues: object, notifyAgent: boolean, options: SubmitFormOptions) => {
    if (!campaign?.campaignId || !campaign?.state) return;

    submitFormValues(
      {
        CampaignId: campaign.campaignId,
        FormType: formType,
        State: campaign.state as unknown as AustralianState,
        BlankContractIsLive: !campaign.blankContractIsLive,
        NotifyAgent: notifyAgent,
        FormData: formValues,
        getAuthToken,
      },
      {
        onSuccess: async (data) => {
          await queryClient.invalidateQueries({
            queryKey: formConfigQueryKey,
          });

          if (options?.onSuccess) options.onSuccess(data);
        },
        onError: options?.onError,
      }
    );
  };

  return {
    formConfig,
    isLoading,
    isFetched,
    saveForm,
    submitForm,
  };
};

export default useFormConfig;
